import { FC } from "react";
import { Button, Typography, Card, CardContent, CardHeader, Checkbox } from "@altimateai/lego";
import { Stack } from "@altimateai/lego";
import { IconDatabase, IconBook } from "@tabler/icons-react";

interface DataMate {
  id?: string | number;
  name: string;
}

interface KnowledgeBase {
  id: number;
  name: string;
}

interface EvaluationContextSelectorProps {
  datamates: DataMate[];
  knowledgeBases: KnowledgeBase[];
  selectedDatamates: string[];
  selectedKnowledgeBases: number[];
  onDatamatesChange: (datamates: string[]) => void;
  onKnowledgeBasesChange: (kbs: number[]) => void;
}

export const EvaluationContextSelector: FC<EvaluationContextSelectorProps> = ({
  datamates,
  knowledgeBases,
  selectedDatamates,
  selectedKnowledgeBases,
  onDatamatesChange,
  onKnowledgeBasesChange,
}) => {
  return (
    <Card className="al-mt-4">
      <CardHeader>
        <Typography size="regular" weight="medium">
          Context (Optional)
        </Typography>
        <Typography size="sm" className="al-text-gray-600 al-mt-1">
          Select DataMates and Knowledge Bases to be available for all agents
        </Typography>
      </CardHeader>
      <CardContent>
        <div className="al-grid al-grid-cols-2 al-gap-4">
          {/* DataMates Selection */}
          <div>
            <Stack direction="row" justify="between" align="center" className="al-mb-2">
              <Typography size="sm" weight="medium">
                DataMates ({selectedDatamates.length}/{datamates.length})
              </Typography>
              <Stack direction="row" gap="xs">
                <Button
                  onClick={() => onDatamatesChange(datamates.map(dm => String(dm.id)))}
                  variant="ghost"
                  size="sm"
                  className="al-text-xs"
                >
                  Select All
                </Button>
                <Button
                  onClick={() => onDatamatesChange([])}
                  variant="ghost"
                  size="sm"
                  className="al-text-xs"
                >
                  Clear
                </Button>
              </Stack>
            </Stack>
            <div className="al-max-h-48 al-overflow-y-auto al-space-y-2 al-border al-rounded-md al-p-3">
              {datamates.length === 0 ? (
                <div className="al-text-center al-py-4 al-text-sm al-text-gray-500">
                  No datamates available
                </div>
              ) : (
                datamates.map(dm => (
                  <div
                    key={dm.id}
                    role="button"
                    tabIndex={0}
                    className="al-p-2 hover:al-bg-gray-50 al-rounded al-cursor-pointer"
                    onClick={() =>
                      onDatamatesChange(
                        selectedDatamates.includes(String(dm.id))
                          ? selectedDatamates.filter(id => id !== String(dm.id))
                          : [...selectedDatamates, String(dm.id)]
                      )
                    }
                    onKeyDown={e => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        onDatamatesChange(
                          selectedDatamates.includes(String(dm.id))
                            ? selectedDatamates.filter(id => id !== String(dm.id))
                            : [...selectedDatamates, String(dm.id)]
                        );
                      }
                    }}
                  >
                    <Stack direction="row" align="center" gap="xs">
                      <Checkbox
                        checked={selectedDatamates.includes(String(dm.id))}
                        onCheckedChange={() =>
                          onDatamatesChange(
                            selectedDatamates.includes(String(dm.id))
                              ? selectedDatamates.filter(id => id !== String(dm.id))
                              : [...selectedDatamates, String(dm.id)]
                          )
                        }
                        onClick={(e: React.MouseEvent) => e.stopPropagation()}
                      />
                      <IconDatabase size={16} className="al-text-gray-500" />
                      <Typography size="sm">{dm.name}</Typography>
                    </Stack>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Knowledge Bases Selection */}
          <div>
            <Stack direction="row" justify="between" align="center" className="al-mb-2">
              <Typography size="sm" weight="medium">
                Knowledge Bases ({selectedKnowledgeBases.length}/{knowledgeBases.length})
              </Typography>
              <Stack direction="row" gap="xs">
                <Button
                  onClick={() => onKnowledgeBasesChange(knowledgeBases.map(kb => kb.id))}
                  variant="ghost"
                  size="sm"
                  className="al-text-xs"
                >
                  Select All
                </Button>
                <Button
                  onClick={() => onKnowledgeBasesChange([])}
                  variant="ghost"
                  size="sm"
                  className="al-text-xs"
                >
                  Clear
                </Button>
              </Stack>
            </Stack>
            <div className="al-max-h-48 al-overflow-y-auto al-space-y-2 al-border al-rounded-md al-p-3">
              {knowledgeBases.length === 0 ? (
                <div className="al-text-center al-py-4 al-text-sm al-text-gray-500">
                  No knowledge bases available
                </div>
              ) : (
                knowledgeBases.map(kb => (
                  <div
                    key={kb.id}
                    role="button"
                    tabIndex={0}
                    className="al-p-2 hover:al-bg-gray-50 al-rounded al-cursor-pointer"
                    onClick={() =>
                      onKnowledgeBasesChange(
                        selectedKnowledgeBases.includes(kb.id)
                          ? selectedKnowledgeBases.filter(id => id !== kb.id)
                          : [...selectedKnowledgeBases, kb.id]
                      )
                    }
                    onKeyDown={e => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        onKnowledgeBasesChange(
                          selectedKnowledgeBases.includes(kb.id)
                            ? selectedKnowledgeBases.filter(id => id !== kb.id)
                            : [...selectedKnowledgeBases, kb.id]
                        );
                      }
                    }}
                  >
                    <Stack direction="row" align="center" gap="xs">
                      <Checkbox
                        checked={selectedKnowledgeBases.includes(kb.id)}
                        onCheckedChange={() =>
                          onKnowledgeBasesChange(
                            selectedKnowledgeBases.includes(kb.id)
                              ? selectedKnowledgeBases.filter(id => id !== kb.id)
                              : [...selectedKnowledgeBases, kb.id]
                          )
                        }
                        onClick={(e: React.MouseEvent) => e.stopPropagation()}
                      />
                      <IconBook size={16} className="al-text-gray-500" />
                      <Typography size="sm">{kb.name}</Typography>
                    </Stack>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
